/* File: frontend/src/components/UserManagement.js */
/* Version: 3.4 (Added Managing Partner Support) */
/* Description: Includes 'Partner' role in creation modal, filtering, and management permissions. */

import React, { useState, useEffect, useCallback } from 'react';
import InputMask from 'react-input-mask';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import LoadingSpinner from './LoadingSpinner';
import './UserManagement.css';
import {
    FiBriefcase, FiUsers, FiCalendar, FiArchive,
    FiUserPlus, FiTrash2, FiX, FiEdit2, FiLock, FiSearch
} from 'react-icons/fi';

import ResetPasswordModal from './ResetPasswordModal';

const TIMEZONE_OPTIONS = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Phoenix", label: "Arizona (MST)" },
    { value: "UTC", label: "UTC" },
];

// --- HELPERS ---
const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return 'Invalid Date'; }
};

const formatPhone = (phoneString) => {
    if (!phoneString || phoneString.length < 10) return phoneString || '';
    const digits = phoneString.replace(/\D/g, '');
    if (digits.startsWith('1') && digits.length === 11) {
        return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
    }
    if (digits.length === 10) {
        return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    return phoneString;
};

// --- SUB-COMPONENT: Client Case Info ---
const ClientCaseInfo = ({ user }) => {
    const { latest_case, next_deadline } = user;
    if (!latest_case) return <div className="meta-row text-muted"><FiArchive className="meta-icon" /> No active case</div>;

    return (
        <div className="client-case-meta">
            <div className="meta-row">
                <FiBriefcase className="meta-icon" />
                <span className="meta-label">Case:</span>
                <Link to={`/cases/${latest_case.id}`} className="meta-link">{latest_case.title}</Link>
                <span className="badge-mini pending">{latest_case.status.replace(/_/g, ' ')}</span>
            </div>
            {(latest_case.assigned_attorney || latest_case.assigned_paralegal) && (
                <div className="meta-row text-small">
                    <FiUsers className="meta-icon" />
                    <span>
                        {latest_case.assigned_attorney ? `Atty: ${latest_case.assigned_attorney.last_name}` : ''}
                        {latest_case.assigned_attorney && latest_case.assigned_paralegal ? ', ' : ''}
                        {latest_case.assigned_paralegal ? `Para: ${latest_case.assigned_paralegal.last_name}` : ''}
                    </span>
                </div>
            )}
            {next_deadline?.due_date && (
                <div className="meta-row deadline-row">
                    <FiCalendar className="meta-icon deadline" />
                    <span className="meta-label">Due:</span>
                    <span className="deadline-date">{formatDate(next_deadline.due_date)}</span>
                </div>
            )}
        </div>
    );
};

// --- SUB-COMPONENT: User List (With Correct Permissions) ---
const UserList = ({ users, currentUser, activeTab, handleDeleteUser, onEditUser, onResetPassword }) => {
    // 1. Права "Суперпользователей" (видят всё)
    const currentUserRoles = new Set(currentUser?.roles?.map(r => r.name) || []);
    const isSuperUser = currentUserRoles.has('ADMINISTRATOR') || currentUserRoles.has('MANAGING_PARTNER') || currentUserRoles.has('CREATOR') || currentUserRoles.has('SUPERADMIN');

    if (!users || users.length === 0) {
        return (
            <div className="empty-state-platinum">
                <h3>No users found</h3>
                <p>Try adjusting your search or filters.</p>
            </div>
        );
    }

    return (
        <div className="user-cards-grid">
            {users.map(user => {
                const isClient = activeTab === 'clients';

                // 2. ЛОГИКА ДОСТУПА К ПРОФИЛЮ КЛИЕНТА
                let canViewProfile = false;
                if (isClient) {
                    // а) Суперпользователь всегда имеет доступ
                    if (isSuperUser) canViewProfile = true;
                    // б) Если я назначен на дело этого клиента (Atttorney или Paralegal)
                    else if (user.latest_case) {
                        const assignedAttyId = user.latest_case.assigned_attorney?.id;
                        const assignedParaId = user.latest_case.assigned_paralegal?.id;
                        // Сравниваем ID как строки для надежности
                        if (String(assignedAttyId) === String(currentUser.id) || String(assignedParaId) === String(currentUser.id)) {
                            canViewProfile = true;
                        }
                    }
                }

                // 3. Логика управления сотрудниками (только Админы могут править других юристов)
                // [UPDATED] Добавлена проверка на MANAGING_PARTNER, чтобы можно было редактировать партнера
                const managedRoles = ['ATTORNEY', 'PARALEGAL', 'MANAGING_PARTNER', 'ADMINISTRATOR', 'CLIENT'];
                const canBeManaged = isSuperUser && (
                    currentUserRoles.has('CREATOR') || currentUserRoles.has('SUPERADMIN') || user.roles.some(r => managedRoles.includes(r.name))
                );

                return (
                    <div key={user.id} className="user-card-platinum">
                        {/* LEFT: Avatar & Info */}
                        <div className="card-left">
                            <div className="user-avatar-circle">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                            <div className="user-main-info">
                                <h3 className="user-name">{user.first_name} {user.last_name}</h3>
                                <div className="user-contact">
                                    <span className="user-email">{user.email}</span>
                                    {user.phone_number && (
                                        <span className="user-phone-bullet">• {formatPhone(user.phone_number)}</span>
                                    )}
                                </div>
                                {isClient && <ClientCaseInfo user={user} />}
                            </div>
                        </div>

                        {/* RIGHT: Actions */}
                        <div className="card-right">
                            <div className="roles-container">
                                {user.roles.map(role => (
                                    <span key={role.name} className="role-pill-clean">
                                        {role.name.replace(/_/g, ' ')}
                                    </span>
                                ))}
                            </div>

                            <div className="actions-row">
                                {/* Кнопка профиля (с проверкой прав) */}
                                {canViewProfile && (
                                    <Link to={`/clients/${user.id}`} className="btn-platinum-outline">
                                        View Profile
                                    </Link>
                                )}

                                {/* Кнопки управления (только для админов) */}
                                {canBeManaged && (
                                    <>
                                        <button onClick={() => onResetPassword(user)} className="icon-btn-platinum warning" title="Reset Password">
                                            <FiLock />
                                        </button>
                                        <button onClick={() => onEditUser(user)} className="icon-btn-platinum secondary" title="Edit">
                                            <FiEdit2 />
                                        </button>
                                        <button onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)} className="icon-btn-platinum danger" title="Delete">
                                            <FiTrash2 />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// --- SUB-COMPONENT: Employee Modal ---
const EmployeeModal = ({ isOpen, onClose, onAdd, onEdit, initialData }) => {
    const isEditMode = Boolean(initialData);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [role, setRole] = useState('ATTORNEY');
    const [timezone, setTimezone] = useState('America/New_York');
    const [password, setPassword] = useState('');
    const [aiPersona, setAiPersona] = useState('colleague');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user: currentUser } = useAuth();

    const isCurrentUserCreator = React.useMemo(() => {
        if (!currentUser) return false;
        const email = currentUser.email?.toLowerCase();
        if (email === 'afitbk@gmail.com' || email === 'sakovichkatebk@gmail.com') return true;
        const roleNames = (currentUser.roles || []).map(r => typeof r === 'string' ? r : (r.name || '').toUpperCase());
        return roleNames.includes('CREATOR') || roleNames.includes('SUPERADMIN') || roleNames.includes('ADMINISTRATOR');
    }, [currentUser]);

    useEffect(() => {
        console.log("EmployeeModal: currentUser is", currentUser);
        console.log("EmployeeModal: isCurrentUserCreator is", isCurrentUserCreator);
    }, [currentUser, isCurrentUserCreator]);
    
    // [NEW] ALL 14 TABS CONFIGURATION
    const ALL_TABS = [
        { key: 'cases', icon: '💼', label: 'Cases', desc: 'Case Management', color: '#3b82f6' },
        { key: 'ai_paralegal', icon: '⚖️', label: 'AI Paralegal', desc: 'Auto-Drafting', color: '#6366f1' },
        { key: 'tasks', icon: '✅', label: 'Tasks', desc: 'Task Manager', color: '#22c55e' },
        { key: 'calendar', icon: '📅', label: 'Calendar', desc: 'Events & Meetings', color: '#ef4444' },
        { key: 'leads', icon: '🎯', label: 'Leads', desc: 'CRM & Pipelines', color: '#f43f5e' },
        { key: 'billing', icon: '💳', label: 'Billing', desc: 'Invoices & Payments', color: '#14b8a6' },
        { key: 'reputation', icon: '⭐', label: 'Reputation', desc: 'Reviews & Brand', color: '#f59e0b' },
        { key: 'users', icon: '👥', label: 'User Management', desc: 'Staff & Roles', color: '#3b82f6' },
        { key: 'katifi_assistant', icon: '👁️', label: 'AI Assistant (Julifi)', desc: 'Global Chat Widget', color: '#a855f7' },
        { key: 'profile', icon: '👤', label: 'Profile', desc: 'Account Settings', color: '#64748b' },
        { key: 'voice_ai', icon: '🎙️', label: 'Voice AI', desc: 'Call Monitor', color: '#10b981' },
        { key: 'organic_seo', icon: '🌐', label: 'Organic SEO', desc: 'Manage SEO', color: '#d97706' },
        { key: 'ai_staff', icon: '🤖', label: 'AI Staff', desc: 'Manage AI agents', color: '#1e40af' },
        { key: 'magic_deploy', icon: '✨', label: 'Magic Deploy', desc: 'URL Automation', color: '#6b21a8' },
        { key: 'brain_center', icon: '🧠', label: 'Brain Center', desc: 'System Brain & AI Control', color: '#8b5cf6' }
    ];

    const BRAIN_SUB_TABS = [
        { key: 'brain_creator', icon: '✨', label: 'Create or Re-design', desc: 'Site Creator & Architect', color: '#14b8a6' },
        { key: 'brain_katifi', icon: '🧿', label: 'Katifi/Julifi AI', desc: 'System AI Manager Chat', color: '#6366f1' },
        { key: 'brain_neural_wiring', icon: '🧠', label: 'Neural Wiring', desc: 'Edit Prompts & Core System', color: '#7c3aed' },
        { key: 'brain_phoenix', icon: '🦅', label: 'Operation Phoenix', desc: 'Remaster Legacy Content', color: '#f59e0b' },
        { key: 'brain_warfare', icon: '📜', label: 'Content Warfare', desc: 'Global Strategy Tasks', color: '#ef4444' },
        { key: 'brain_reality_override', icon: '🔮', label: 'Reality Override', desc: 'Override Settings/Config', color: '#8b5cf6' },
        { key: 'brain_corrections', icon: '🛡️', label: 'AI Corrections', desc: 'Review & Fix AI Errors', color: '#10b981' },
        { key: 'brain_virtual_office', icon: '🏢', label: 'The Virtual Office', desc: 'AI Staff Dashboard', color: '#1e40af' },
        { key: 'brain_memory', icon: '🧠', label: 'Memory Panel', desc: 'View Katifi Memory Logs', color: '#6366f1' },
        { key: 'brain_cleanup', icon: '🧹', label: 'Site Cleanup', desc: 'Clean PBN & Web Footprints', color: '#14b8a6' },
        { key: 'brain_taskboard', icon: '📋', label: 'Task Board', desc: 'AI Task Management Board', color: '#ec4899' },
        { key: 'brain_videoarchive', icon: '🔐', label: 'Видео Архив', desc: 'Video Archive (Creator)', color: '#4c1d95' }
    ];

    const DEFAULT_NEW_USER_TABS = {
        ...ALL_TABS.reduce((acc, tab) => {
            acc[tab.key] = tab.key === 'reputation';
            return acc;
        }, {}),
        ...BRAIN_SUB_TABS.reduce((acc, sub) => {
            acc[sub.key] = false;
            return acc;
        }, {})
    };

    const [allowedTabs, setAllowedTabs] = useState(DEFAULT_NEW_USER_TABS);

    const handleTabToggle = (key) => {
        setAllowedTabs(prev => {
            const next = { ...prev, [key]: !prev[key] };
            if (key === 'brain_center' && prev[key] === true) {
                // When toggling Brain Center OFF, clear all sub-tab permissions to false
                BRAIN_SUB_TABS.forEach(sub => {
                    next[sub.key] = false;
                });
            }
            return next;
        });
    };

    useEffect(() => {
        if (isOpen) {
            if (isEditMode) {
                setFirstName(initialData.first_name || '');
                setLastName(initialData.last_name || '');
                setEmail(initialData.email || '');
                setPhone(initialData.phone_number || '');
                const staffRole = initialData.roles.find(r => r.name === 'ATTORNEY' || r.name === 'PARALEGAL' || r.name === 'MANAGING_PARTNER');
                setRole(staffRole ? staffRole.name : 'ATTORNEY');
                setTimezone(initialData.timezone || 'America/New_York');
                
                // Parse existing tab rules. If empty JSON, default to ALL TRUE for safety or the explicit default.
                const userTabs = initialData.allowed_tabs || {};
                
                if (Object.keys(userTabs).length === 0) {
                     setAllowedTabs(DEFAULT_NEW_USER_TABS);
                } else {
                     // Ensure all 14 keys exist in state
                     const merged = { ...DEFAULT_NEW_USER_TABS };
                     Object.keys(userTabs).forEach(k => merged[k] = userTabs[k]);
                     setAllowedTabs(merged);
                }
                setAiPersona(initialData.ai_persona || 'colleague');
            } else {
                setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setRole('ATTORNEY'); setTimezone('America/New_York'); setPassword('');
                setAllowedTabs(DEFAULT_NEW_USER_TABS);
                setAiPersona('colleague');
            }
            setIsSubmitting(false);
        }
    }, [isOpen, initialData, isEditMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const cleanPhone = phone ? phone.replace(/[^\d+]/g, '') : null;
            const finalAllowedTabs = { ...allowedTabs };
            if (email?.toLowerCase() !== 'afitbk@gmail.com') {
                delete finalAllowedTabs['brain_videoarchive'];
            }
            const userData = { 
                first_name: firstName, 
                last_name: lastName, 
                email, 
                phone_number: cleanPhone, 
                roles: [role], 
                timezone,
                allowed_tabs: finalAllowedTabs,
                ai_persona: aiPersona
            };
            if (!isEditMode && password) {
                userData.password = password;
            }
            if (isEditMode) await onEdit(initialData.id, userData);
            else await onAdd(userData);
            onClose();
        } catch (error) {
            // Error handled by parent
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content add-employee-modal" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="modal-close-button"><FiX /></button>
                <h2>{isEditMode ? 'Edit Employee' : 'Add New Employee'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group-row">
                        <div className="form-group half">
                            <label>First Name *</label>
                            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                        </div>
                        <div className="form-group half">
                            <label>Last Name</label>
                            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Email *</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <InputMask mask="+1 (999) 999-9999" value={phone} onChange={(e) => setPhone(e.target.value)}>
                            {(inputProps) => <input {...inputProps} type="tel" placeholder="+1 (___) ___-____" />}
                        </InputMask>
                    </div>
                    {!isEditMode && (
                        <div className="form-group">
                            <label>Password *</label>
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
                        </div>
                    )}
                    <div className="form-group">
                        <label>Timezone *</label>
                        <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                            {TIMEZONE_OPTIONS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Role *</label>
                        <select value={role} onChange={(e) => setRole(e.target.value)}>
                            {/* [UPDATED] Добавлена опция MANAGING_PARTNER */}
                            <option value="MANAGING_PARTNER">Managing Partner (Firm Owner)</option>
                            <option value="ATTORNEY">Attorney</option>
                            <option value="PARALEGAL">Paralegal</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>🤖 AI Assistant Persona (DM & Spies)</label>
                        <select value={aiPersona} onChange={(e) => setAiPersona(e.target.value)}>
                            <option value="colleague">Colleague (Professional & Respectful)</option>
                            <option value="friend">Friend (Casual & Warm)</option>
                            <option value="close_friend">Close Friend (Empathetic & Very Open)</option>
                            <option value="romantic">Romantic (Flirtatious & Devoted)</option>
                        </select>
                    </div>

                    <div className="form-group" style={{ marginTop: '25px', backgroundColor: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <h4 style={{ fontSize: '1.1rem', color: '#0f172a', margin: '0 0 4px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                🛡️ Employee Tab Permissions
                            </h4>
                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>Green indicates the module is strictly visible to this employee's sidebar menu.</p>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {ALL_TABS.map((tab) => {
                                if (tab.key === 'brain_center' && !isCurrentUserCreator) {
                                    return null;
                                }
                                const isAllowed = allowedTabs[tab.key];
                                return (
                                    <div 
                                        key={tab.key}
                                        onClick={() => handleTabToggle(tab.key)}
                                        style={{ 
                                            padding: '12px', 
                                            borderRadius: '10px', 
                                            border: isAllowed ? `2px solid ${tab.color}` : '1px solid #cbd5e1', 
                                            backgroundColor: isAllowed ? '#f8fafc' : '#f8fafc', 
                                            cursor: 'pointer', 
                                            transition: 'all 0.2s',
                                            boxShadow: isAllowed ? `0 0 0 1px ${tab.color}20` : 'none'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
                                            <input 
                                                type="checkbox" 
                                                checked={isAllowed} 
                                                readOnly 
                                                style={{ accentColor: tab.color, width: '16px', height: '16px', cursor: 'pointer' }} 
                                            />
                                        </div>
                                        <h5 style={{ margin: '0 0 2px 0', fontSize: '0.90rem', color: isAllowed ? tab.color : '#475569', fontWeight: '600' }}>
                                            {tab.label}
                                        </h5>
                                        <p style={{ margin: 0, fontSize: '0.70rem', color: '#64748b' }}>
                                            {tab.desc}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {allowedTabs['brain_center'] && isCurrentUserCreator && (
                            <div style={{ marginTop: '20px', borderTop: '1px dashed #cbd5e1', paddingTop: '16px' }}>
                                <h4 style={{ fontSize: '1.0rem', color: '#0f172a', margin: '0 0 12px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    🧠 Brain Center Sub-Permissions
                                </h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    {BRAIN_SUB_TABS.filter((subTab) => {
                                        if (subTab.key === 'brain_videoarchive') {
                                            return email?.toLowerCase() === 'afitbk@gmail.com';
                                        }
                                        return true;
                                    }).map((subTab) => {
                                        const isSubAllowed = allowedTabs[subTab.key];
                                        return (
                                            <div 
                                                key={subTab.key}
                                                onClick={() => handleTabToggle(subTab.key)}
                                                style={{ 
                                                    padding: '10px', 
                                                    borderRadius: '8px', 
                                                    border: isSubAllowed ? `2px solid ${subTab.color}` : '1px solid #cbd5e1', 
                                                    backgroundColor: isSubAllowed ? '#f8fafc' : '#f8fafc', 
                                                    cursor: 'pointer', 
                                                    transition: 'all 0.2s',
                                                    boxShadow: isSubAllowed ? `0 0 0 1px ${subTab.color}20` : 'none'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2px' }}>
                                                    <span style={{ fontSize: '1.1rem' }}>{subTab.icon}</span>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isSubAllowed} 
                                                        readOnly 
                                                        style={{ accentColor: subTab.color, width: '14px', height: '14px', cursor: 'pointer' }} 
                                                    />
                                                </div>
                                                <h6 style={{ margin: '0 0 2px 0', fontSize: '0.80rem', color: isSubAllowed ? subTab.color : '#475569', fontWeight: '600' }}>
                                                    {subTab.label}
                                                </h6>
                                                <p style={{ margin: 0, fontSize: '0.65rem', color: '#64748b' }}>
                                                    {subTab.desc}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-actions" style={{ marginTop: '25px' }}>
                        <button type="button" onClick={onClose} className="btn-platinum-outline" disabled={isSubmitting}>Cancel</button>
                        <button type="submit" className="btn-dark" disabled={isSubmitting}>
                            {isSubmitting ? <LoadingSpinner size="sm" /> : (isEditMode ? 'Save Changes' : 'Add Employee')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: Add Client Modal ---
const ClientModal = ({ isOpen, onClose, onAdd }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFirstName(''); setLastName(''); setEmail(''); setPhone('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const cleanPhone = phone ? phone.replace(/[^\d+]/g, '') : null;
            const userData = {
                first_name: firstName,
                last_name: lastName,
                email,
                phone_number: cleanPhone,
                roles: ['CLIENT'],
            };
            await onAdd(userData);
            onClose();
        } catch (error) {
            // Error handled by parent
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content add-employee-modal" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="modal-close-button"><FiX /></button>
                <h2>Add New Client</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group-row">
                        <div className="form-group half">
                            <label>First Name *</label>
                            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                        </div>
                        <div className="form-group half">
                            <label>Last Name</label>
                            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Email *</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <InputMask mask="+1 (999) 999-9999" value={phone} onChange={(e) => setPhone(e.target.value)}>
                            {(inputProps) => <input {...inputProps} type="tel" placeholder="+1 (___) ___-____" />}
                        </InputMask>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0 }}>
                        A temporary password will be generated and emailed to the client.
                    </p>
                    <div className="modal-actions" style={{ marginTop: '25px' }}>
                        <button type="button" onClick={onClose} className="btn-platinum-outline" disabled={isSubmitting}>Cancel</button>
                        <button type="submit" className="btn-dark" disabled={isSubmitting}>
                            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Add Client'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [accessError, setAccessError] = useState('');
    const [activeTab, setActiveTab] = useState('clients');
    const [activeSubTab, setActiveSubTab] = useState('ATTORNEY');
    const [searchTerm, setSearchTerm] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [passwordResetUser, setPasswordResetUser] = useState(null);

    const { user: currentUser } = useAuth();

    // Получаем список ролей текущего пользователя
    const currentUserRoles = new Set(currentUser?.roles?.map(role => role.name) || []);
    // Проверка на админа/партнера (для показа кнопки Add Employee и доступа ко всем)
    const canManageUsers = currentUserRoles.has('ADMINISTRATOR') || currentUserRoles.has('MANAGING_PARTNER') || currentUserRoles.has('CREATOR') || currentUserRoles.has('SUPERADMIN');
    // Проверка на просмотр (для загрузки списка) - по сути то же самое, но может быть расширено
    const canViewClientList = true; // Список клиентов могут видеть все сотрудники

    const loadUsersForTab = useCallback(async () => {
        setLoading(true); setAccessError(''); setUsers([]);
        try {
            let usersData = [];
            if (activeTab === 'office') {
                usersData = await apiService.getUsersByRole(activeSubTab);
            } else if (activeTab === 'clients') {
                if (!canViewClientList) throw new Error('403 Forbidden');
                usersData = await apiService.getClientsWithDetails();
            }
            setUsers(usersData || []);
        } catch (error) {
            if (error.message?.includes('403')) setAccessError('Permission denied.');
            else toast.error(`Failed to load users: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [activeTab, activeSubTab, canViewClientList]);

    useEffect(() => { loadUsersForTab(); }, [loadUsersForTab]);

    const handleAddNewUser = async (data) => {
        try {
            await apiService.addUser(data);
            toast.success('Employee added!');
            loadUsersForTab();
        } catch (e) { toast.error(e.message); throw e; }
    };

    const handleAddNewClient = async (data) => {
        try {
            await apiService.addUser(data);
            toast.success('Client added!');
            setActiveTab('clients');
            loadUsersForTab();
        } catch (e) { toast.error(e.message); throw e; }
    };

    const handleUpdateUser = async (id, data) => {
        try {
            await apiService.updateUser(id, data);
            toast.success('Updated successfully');
            loadUsersForTab();
        } catch (e) { toast.error(e.message); throw e; }
    };

    const handleDeleteUser = async (id, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        try {
            await apiService.deleteUser(id);
            toast.success('Deleted');
            loadUsersForTab();
        } catch (e) { toast.error(e.message); }
    };

    const filteredUsers = users.filter(u => {
        if (!searchTerm) return true;
        const lower = searchTerm.toLowerCase();
        return u.first_name?.toLowerCase().includes(lower) || u.last_name?.toLowerCase().includes(lower) || u.email?.toLowerCase().includes(lower);
    });

    return (
        <div className="user-management-container">
            {/* HEADER */}
            <div className="page-header-flex">
                <div className="header-titles">
                    <h1 className="page-title">User Management</h1>
                    <p className="page-subtitle">Manage system users, clients, and their access roles.</p>
                </div>
                {canManageUsers && (
                    <div style={{ display: 'flex', gap: 12 }}>
                        {activeTab === 'clients' && (
                            <button onClick={() => setIsClientModalOpen(true)} className="btn-platinum-outline">
                                <FiUserPlus size={18} style={{ marginRight: 8 }} /> Add Client
                            </button>
                        )}
                        <button onClick={() => { setEditingUser(null); setIsModalOpen(true); }} className="btn-dark">
                            <FiUserPlus size={18} style={{ marginRight: 8 }} /> Add Employee
                        </button>
                    </div>
                )}
            </div>

            {/* CONTROLS */}
            <div className="controls-row">
                <div className="tabs-container">
                    <button onClick={() => setActiveTab('clients')} className={`tab-link ${activeTab === 'clients' ? 'active' : ''}`}>Clients</button>
                    <button onClick={() => setActiveTab('office')} className={`tab-link ${activeTab === 'office' ? 'active' : ''}`}>Office Staff</button>
                </div>
                <div className="search-wrapper">
                    <FiSearch className="search-icon" />
                    <input type="text" placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
            </div>

            {/* SUBTABS (Office Only) */}
            {activeTab === 'office' && (
                <div className="subtabs-row">
                    {/* [UPDATED] Добавлена вкладка Partners */}
                    <button onClick={() => setActiveSubTab('MANAGING_PARTNER')} className={`subtab-pill ${activeSubTab === 'MANAGING_PARTNER' ? 'active' : ''}`}>Partners</button>
                    <button onClick={() => setActiveSubTab('ATTORNEY')} className={`subtab-pill ${activeSubTab === 'ATTORNEY' ? 'active' : ''}`}>Attorneys</button>
                    <button onClick={() => setActiveSubTab('PARALEGAL')} className={`subtab-pill ${activeSubTab === 'PARALEGAL' ? 'active' : ''}`}>Paralegals</button>
                </div>
            )}

            {/* LIST */}
            {loading ? <LoadingSpinner message="Loading..." /> : accessError ? <div className="error-state">{accessError}</div> : (
                <UserList
                    users={filteredUsers}
                    currentUser={currentUser} /* Передаем текущего пользователя для проверки прав */
                    activeTab={activeTab}
                    handleDeleteUser={handleDeleteUser}
                    onEditUser={(u) => { setEditingUser(u); setIsModalOpen(true); }}
                    onResetPassword={(u) => { setPasswordResetUser(u); setIsPasswordModalOpen(true); }}
                />
            )}

            {/* MODALS */}
            <EmployeeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={handleAddNewUser} onEdit={handleUpdateUser} initialData={editingUser} />
            <ClientModal isOpen={isClientModalOpen} onClose={() => setIsClientModalOpen(false)} onAdd={handleAddNewClient} />
            {isPasswordModalOpen && passwordResetUser && (
                <ResetPasswordModal user={passwordResetUser} onClose={() => { setIsPasswordModalOpen(false); setPasswordResetUser(null); }} />
            )}
        </div>
    );
};

export default UserManagement;